import { Injectable } from '@angular/core';
import { AuthenticationService } from '../authentication/authentication.service';
import { authModel, storageApiKey } from '../../models/auth';
import { HttpRequest, HttpHandler, HttpInterceptor, HttpErrorResponse } from "@angular/common/http";
import { Observable, BehaviorSubject, throwError, of } from "rxjs";
import { catchError, switchMap, finalize, filter, take, map } from "rxjs/operators";

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
    constructor(public authenticationService: AuthenticationService) { }

    public isRefreshing = false;
    public tokenSubject: BehaviorSubject<authModel | null> = new BehaviorSubject<authModel | null>(null);

    public intercept(request: HttpRequest<any>, next: HttpHandler): Observable<any> {
        const re = /login|oauth|authentication/gi;

        if (request.url.search(re) !== -1) {
            return next.handle(request);
        }

        const domain = request.url.replace('http://', '').replace('https://', '').split(/[/?#]/)[0];
        if (['api.coinbase.com', 'api.ipify.org', 'cors-anywhere.herokuapp.com', 'www.verkoopmonitor.nl'].includes(domain)) {
            return next.handle(request);
        }

        const authData = localStorage.getItem("auth");
        if (!authData) {
            return throwError(() => new HttpErrorResponse({ status: 401 }));
        }
        const currentUser = this.safeJsonParse<authModel>(authData);
        if (!currentUser) {
            return throwError(() => new HttpErrorResponse({ status: 401 }));
        }
        return of(currentUser).pipe(
            map((currentUser: authModel) => {
                if (!this.authenticationService.checkAccessTokenValidity()) {
                    throw new HttpErrorResponse({ status: 401 });
                }
                return currentUser.access_token;
            }),
            switchMap((token) => {
                if (!this.isRefreshing) {
                    const tokenReq = request.clone({
                        setHeaders: {
                            Authorization: `Bearer ${token}`
                        }
                    });
                    return next.handle(tokenReq);
                }
                return next.handle(request);
            }),
            catchError(err => {
                if (err instanceof HttpErrorResponse && err.status === 401) {
                    return this.handleUnauthorized(request, next);
                }
                return throwError(() => err);
            })
        );
    }

    private handleUnauthorized(request: HttpRequest<any>, next: HttpHandler): Observable<any> {
        if (!this.isRefreshing) {
            const authData = localStorage.getItem("auth");
            if (!authData) {
                this.doLogout();
                return throwError(() => new Error('No auth data found'));
            }
            const currentUser = this.safeJsonParse<authModel>(authData);
            if (!currentUser) {
                this.doLogout();
                return throwError(() => new Error('Invalid auth data'));
            }
            this.isRefreshing = true;
            this.tokenSubject.next(null);

            if (this.authenticationService.checkRefreshTokenValidity()) {
                return this.authenticationService.loginWithRefreshToken(currentUser.refresh_token).pipe(
                    switchMap((newAuthModel: authModel | null) => {
                        if (!newAuthModel || !newAuthModel.access_token) {
                            this.doLogout();
                            return throwError(() => new Error('No new token received'));
                        }
                        const newToken = newAuthModel.access_token;
                        this.tokenSubject.next(newAuthModel);
                        const tokenReq = request.clone({
                            setHeaders: {
                                Authorization: `Bearer ${newToken}`
                            }
                        });
                        return next.handle(tokenReq);
                    }),
                    catchError(error => {
                        this.doLogout();
                        return throwError(() => error);
                    }),
                    finalize(() => {
                        this.isRefreshing = false;
                    })
                );
            }

            if (this.authenticationService.checkApiKeyValidity()) {
                const apiKeyData = localStorage.getItem('apiKey');
                if (!apiKeyData) {
                    this.doLogout();
                    return throwError(() => new Error('No API key found'));
                }
                const apiKey = this.safeJsonParse<storageApiKey>(apiKeyData);
                if (!apiKey) {
                    this.doLogout();
                    return throwError(() => new Error('Invalid API key data'));
                }
                return this.authenticationService.loginWithApiKey(apiKey.api_key, currentUser.username || '', currentUser.device_token || '').pipe(
                    switchMap((newAuthModel: authModel | null) => {
                        if (!newAuthModel || !newAuthModel.access_token) {
                            this.doLogout();
                            return throwError(() => new Error('No new token received'));
                        }
                        const newToken = newAuthModel.access_token;
                        this.tokenSubject.next(newAuthModel);
                        const tokenReq = request.clone({
                            setHeaders: {
                                Authorization: `Bearer ${newToken}`
                            }
                        });
                        return next.handle(tokenReq);
                    }),
                    catchError(error => {
                        this.doLogout();
                        return throwError(() => error);
                    }),
                    finalize(() => {
                        this.authenticationService.requestApiKey(currentUser.device_token || '').subscribe(() => {
                            this.isRefreshing = false;
                        });
                    })
                );
            }

            this.doLogout();
            return throwError(() => new Error('Authentication failed'));
        } else {
            return this.tokenSubject.pipe(
                filter((authModel: authModel | null) => authModel != null),
                take(1),
                switchMap(authModel => {
                    const token = authModel.access_token;
                    const tokenReq = request.clone({
                        setHeaders: {
                            Authorization: `Bearer ${token}`
                        }
                    });
                    return next.handle(tokenReq);
                })
            );
        }
    }

    private doLogout(): void {
        this.authenticationService.logout();
    }

    private safeJsonParse<T>(jsonString: string): T | null {
        try {
            return JSON.parse(jsonString) as T;
        } catch (e) {
            console.error('Failed to parse JSON:', e);
            return null;
        }
    }
}
